import React, { useEffect, useState } from "react";
import { useCustomerContext } from "../../context/CustomerContext";

// API / Models
import { ITicket as ITicketBase } from "redux/reducers/models/ticket";
import {
  GetTickets,
  GetTicketStubPdf,
  PutRedeemMultiple,
  PutVoidTicketStub,
  PostTransferTicketStub,
  GetSharedTickets,
} from "api/rpc/2024-04/facilityAdmin/ticket/ticket";

import { StatusCode } from "api/protocols";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { ITransferDetails, TicketStubType } from "../../context/contextTypes";
import { ICustomer } from "redux/reducers/models/customer";

// Helpers
import { showError, showSuccess } from "redux/actions/ui";
import { valueToString } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import isEmail from "validator/lib/isEmail";
import axios, { CancelToken } from "axios";
import { t } from "i18next";
import moment from "moment";

// Components
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import TicketTransferActionBar from "./components/TicketTransferActionBar/TicketTransferActionBar";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import Checkbox from "components/form/checkbox/Checkbox";
import DataTable from "components/dataTable/DataTable";
import Divider from "components/divider";
import Spin from "components/spin/spin";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import { Badge } from "components/badge/Badge";
import Portal from "elements/Portal";
import { Select } from "components/select/index";

// Icons
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import {
  faArrowTurnDownRight,
  faChevronDown,
  faChevronUp,
  faEllipsisVertical,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Markdown from "components/markdown/Markdown";
import "./customerTickets.scss";
import { PutUnredeemStub } from "api/rpc/2024-04/facilityAdmin/ticket/stub";
import { ButtonNew as Button } from "components/buttonNew";
import useModal from "hooks/modals/useModal";

interface ITicket extends ITicketBase {
  selected?: boolean;
  showTransferBar?: boolean;
}

interface ITicketState {
  tickets: ITicket[];
  selectedTicketStubIDs?: number[];
  selectedTicketID?: number;
}

interface IVoidTicketState {
  openVoidTicket?: boolean;
}

interface IRedeemTicketState {
  openRedeemTicket?: boolean;
}

interface ITransferTicketState {
  openTransferTicket?: boolean;
}

interface IUnredeemTicketState {
  openUnredeemTicket?: boolean;
}

interface ICustomerSearch {
  search: string;
  customers: ICustomer[];
  selectedCustomer: ICustomer;
  showNewRecipient: boolean;
}

interface INewRecipientState {
  firstName: string;
  lastName: string;
  emailAddress: string;
}

const defaultCustomerSearchState: ICustomerSearch = {
  search: "",
  customers: [],
  selectedCustomer: undefined,
  showNewRecipient: false,
};

const defaultNewRecipientState: INewRecipientState = {
  firstName: "",
  lastName: "",
  emailAddress: "",
};

const defaultTicketState: ITicketState = {
  tickets: null,
  selectedTicketStubIDs: [],
  selectedTicketID: null,
};

export default function CustomerTickets(): JSX.Element {
  const dispatch = useAppDispatch();
  const { state } = useCustomerContext();
  const { authStore } = useAppSelector(store => store);

  // State
  const [ticketsLoaded, setTicketsLoaded] = useState<boolean>(false);

  const [voidTicketState, setVoidTicketState] = useState<IVoidTicketState>({
    openVoidTicket: false,
  });

  const [unredeemTicketState, setUnredeemTicketState] = useState<IUnredeemTicketState>({
    openUnredeemTicket: false,
  });
  const [transferTicketState, setTransferTicketState] = useState<ITransferTicketState>({
    openTransferTicket: false,
  });

  const [redeemTicketState, setRedeemTicketState] = useState<IRedeemTicketState>({
    openRedeemTicket: false,
  });

  const {
    state: transferDetails,
    updateModal: updateTransferDetails,
    closeModal: closeTransferDetails,
  } = useModal({
    transferDetails: null as ITransferDetails,
  });

  const [ticketState, setTicketState] = useState<ITicketState>(defaultTicketState);

  const [customerSearchState, setCustomerSearchState] = useState<ICustomerSearch>(defaultCustomerSearchState);
  const [newRecipientState, setNewRecipientState] = useState<INewRecipientState>(defaultNewRecipientState);

  const [expandedStubId, setExpandedStubId] = useState<number | null>(null);
  // End State

  // Use Effects
  useEffect(() => {
    if (state.customer !== undefined || ticketsLoaded === false) {
      void loadTickets();
    }
  }, [state.customer, ticketsLoaded]);

  useEffect(() => {
    if (ticketsLoaded) {
      const source = axios.CancelToken.source();
      void searchCustomer(customerSearchState.search, source.token);
      return () => source.cancel();
    }
  }, [customerSearchState.search]);
  // End Use Effects

  // Loaders
  async function loadTickets() {
    const params = {
      customer_id: state.customer.id,
      extended: true,
    };

    let useSharedTickets = true;
    const [ticketResponse, sharedRes] = await Promise.all([
      GetTickets(params, true),
      GetSharedTickets({ customer_id: state.customer.id }, false),
    ]);

    if (ticketResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Error retrieving tickets")));
      return;
    }

    if (sharedRes?.status !== StatusCode.OK || sharedRes?.data?.length <= 0) {
      if (sharedRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error getting shared tickets")));
      }
      useSharedTickets = false;
    }

    const tickets = ticketResponse.data;
    const sharedTickets: Array<ITicket> = sharedRes?.data;
    {
      tickets?.map((ticket: ITicket) => {
        ticket.stubs?.map((stub: TicketStubType, stubIndex: number) => {
          if (stub.status === "valid") {
            const foundStub = ticket.stubs[stubIndex];
            ticket.stubs.splice(stubIndex, 1);
            ticket.stubs.unshift(foundStub);
          }
        });

        for (let i = 0; i < ticketState.tickets?.length; i++) {
          if (ticket.id === ticketState.tickets[i]?.id) {
            if (ticketState.tickets[i].selected) {
              ticket.selected = true;
            } else {
              ticket.selected = false;
            }
          }
        }
      });
    }

    if (useSharedTickets) {
      sharedTickets?.forEach(sharedTicket => {
        const foundTicketIndex = tickets?.findIndex((ticket: ITicket) => ticket?.id === sharedTicket?.id);
        if (foundTicketIndex !== -1) {
          //Add to existing ticket's stubs
          tickets[foundTicketIndex].stubs = [...tickets[foundTicketIndex].stubs, ...sharedTicket.stubs];
        } else {
          //Add shared ticket
          tickets?.push(sharedTicket);
        }
      });
    }

    setTicketState(prevState => ({ ...prevState, tickets: tickets }));
    setTicketsLoaded(true);
  }

  async function searchCustomer(customerSearchQuery: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: customerSearchQuery }, false, cancelToken);
    setCustomerSearchState(prev => ({ ...prev, customers: customerRes.data }));
  }

  // End Loaders

  // Processors
  async function processVoidTicketStub(ticketStubIDs: number[]) {
    if (ticketStubIDs.length === 0) {
      dispatch(showError(t("No ticket stubs selected for voiding.")));
      return;
    }

    const params = {
      selected_ticket_stub_ids: ticketStubIDs,
    };

    const voidTicketResponse = await PutVoidTicketStub(params, true);
    if (voidTicketResponse.status !== StatusCode.OK) {
      dispatch(showError("Error voiding ticket stub(s)"));
      return;
    }
    dispatch(showSuccess("Ticket stub(s) voided successfully."));

    // Reload tickets after voiding
    setVoidTicketState({ openVoidTicket: false });
    setTicketState(prev => ({ ...prev, selectedTicketStubIDs: [], selectedTicketID: null }));
    await loadTickets();
  }

  async function processRedeemTicketStub(ticketStubIDs: number[]) {
    if (ticketStubIDs.length === 0) {
      dispatch(showError(t("No ticket stubs selected for redeeming.")));
      return;
    }

    const params = {
      ticket_stub_ids: ticketStubIDs,
    };

    const redeemTicketResponse = await PutRedeemMultiple(params, true);

    if (redeemTicketResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Error redeeming ticket stub(s)")));
      return;
    }
    dispatch(showSuccess(t("Ticket stub(s) redeemed successfully.")));

    setRedeemTicketState({ openRedeemTicket: false });
    setTicketState(prev => ({ ...prev, selectedTicketStubIDs: [], selectedTicketID: null }));
    await loadTickets();
  }

  async function processTransferTicketStub(newCustomer = false) {
    let params;
    if (newCustomer) {
      params = {
        selected_ticket_stub_ids: ticketState.selectedTicketStubIDs,
        first_name: newRecipientState.firstName,
        last_name: newRecipientState.lastName,
        email_address: newRecipientState.emailAddress,
        customer_sender_id: state.customer.id,
      };
    } else {
      params = {
        selected_ticket_stub_ids: ticketState.selectedTicketStubIDs,
        first_name: customerSearchState.selectedCustomer.first_name,
        last_name: customerSearchState.selectedCustomer.last_name,
        email_address: customerSearchState.selectedCustomer.email,
        customer_sender_id: state.customer.id,
      };
    }

    const transferTicketResponse = await PostTransferTicketStub(params, true);

    if (transferTicketResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Error transferring ticket stub(s)")));
      return;
    }
    dispatch(showSuccess(t("Ticket stub(s) transferred successfully.")));

    void handleCloseTransferSheet();
    await loadTickets();
  }

  function determineDisable(status: string, shared: boolean) {
    if (shared) {
      return true;
    }

    switch (status) {
      case "void":
      case "expired":
        return true;
      case "valid":
        return false;
      case "redeemed":
        if (authStore.user?.permissions.tickets_unredeem === true) {
          return true;
        } else {
          return false;
        }
      default:
        return false;
    }
  }
  // End Processors

  // Handlers
  async function handleTicketDownload(stub: TicketStubType, format?: "zebra") {
    const ticketStubIds: any = [];
    ticketStubIds.push(stub.id);

    const params = {
      ticket_stub_ids: ticketStubIds,
      format: format,
    };

    const downloadReceiptRes = await GetTicketStubPdf(params, true);

    if (downloadReceiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error downloading ticket stub")));
      return;
    }

    window.open(downloadReceiptRes.data as URL, "_blank");
    return;
  }

  async function handleProcessStubTransfer() {
    const selectedCustomerUndefined = customerSearchState?.selectedCustomer === undefined ? true : false;
    const newRecipientMissingValues = Object.values(newRecipientState).some(value => value === "");

    // Check if a customer has been selected
    if (selectedCustomerUndefined) {
      // Double check new recipient
      if (newRecipientMissingValues) {
        dispatch(showError(t("Please fill in all recipient details")));
        return;
      } else {
        // Process transfer to new recipient
        const newCustomer = true;
        await processTransferTicketStub(newCustomer);
      }
    } else if (newRecipientMissingValues) {
      if (selectedCustomerUndefined) {
        dispatch(showError(t("Please Select a Customer")));
        return;
      } else {
        // Process transfer to selected recipient
        await processTransferTicketStub();
      }
    }
  }

  function handleSelectTicketStub(stub_id: number, ticket_id?: number) {
    let selectedTicketStubIDs = [...ticketState.selectedTicketStubIDs];

    if (ticket_id !== ticketState.selectedTicketID) {
      selectedTicketStubIDs = selectedTicketStubIDs.filter(id => id === stub_id);
    }

    if (selectedTicketStubIDs.includes(stub_id)) {
      const index = selectedTicketStubIDs.indexOf(stub_id);
      if (index > -1) {
        selectedTicketStubIDs.splice(index, 1);
      }
    } else {
      selectedTicketStubIDs.push(stub_id);
    }

    setTicketState(prev => ({ ...prev, selectedTicketStubIDs: selectedTicketStubIDs, selectedTicketID: ticket_id }));
  }

  function handleSelectAllTickets(e: React.ChangeEvent<HTMLInputElement>, ticket_id: number) {
    const selectAllCheckedValue = e.target.checked;
    const selectedTicketIDs = ticketState?.selectedTicketStubIDs;
    const ticket = ticketState.tickets.find(ticket => ticket.id === ticket_id);

    ticket.stubs?.forEach(stub => {
      if (stub.status !== "valid" || stub?.is_shared) {
        return;
      }

      // Avoid flipping selection if the ticket is already selected
      // Select all tickets if checked, otherwise deselect all
      if (selectAllCheckedValue) {
        if (!selectedTicketIDs.includes(stub.id)) {
          selectedTicketIDs.push(stub.id);
        }
      } else {
        const index = selectedTicketIDs?.indexOf(stub.id);
        if (index > -1) {
          selectedTicketIDs.splice(index, 1);
        }
      }
    });

    setTicketState(prev => ({ ...prev, selectedTicketStubIDs: selectedTicketIDs, selectedTicketID: ticket_id }));
  }

  function handleVoidTicketStub(stub?: TicketStubType, actionBar = true) {
    if (stub) {
      // If ticket isn't already selected, add it to the selectedTicketIDs otherwise continue
      if (!ticketState.selectedTicketStubIDs.includes(stub.id)) {
        handleSelectTicketStub(stub.id);
      }
    }

    if (actionBar) {
      if (ticketState.selectedTicketStubIDs.length === 0) {
        dispatch(showError(t("Please select at least one ticket stub to void.")));
        return;
      }
    }
    setVoidTicketState({ openVoidTicket: true });
  }

  function handleRedeemTicketStub(stub?: TicketStubType, actionBar = true) {
    if (stub) {
      // If ticket isn't already selected, add it to the selectedTicketIDs otherwise continue
      if (!ticketState.selectedTicketStubIDs.includes(stub.id)) {
        handleSelectTicketStub(stub.id);
      }
    }

    if (actionBar) {
      if (ticketState.selectedTicketStubIDs.length === 0) {
        dispatch(showError(t("Please select at least one ticket stub to redeem.")));
        return;
      }
    }
    setRedeemTicketState({ openRedeemTicket: true });
  }

  function handleShowTransferSheet(stub?: TicketStubType, actionBar = true) {
    if (stub) {
      if (!ticketState.selectedTicketStubIDs.includes(stub.id)) {
        handleSelectTicketStub(stub.id);
      }
    }

    if (actionBar) {
      if (ticketState.selectedTicketStubIDs.length === 0) {
        dispatch(showError(t("Please select at least one ticket stub to transfer.")));
        return;
      }
    }
    setTransferTicketState(prev => ({ ...prev, openTransferTicket: true }));
  }

  function handleCustomerSelect(value: string, selectedCustomer: ICustomer) {
    if (value == "new") {
      if (isEmail(customerSearchState.search)) {
        setNewRecipientState(prev => ({ ...prev, emailAddress: customerSearchState.search }));
      }
      setCustomerSearchState(prev => ({ ...prev, showNewRecipient: true }));
    } else {
      setCustomerSearchState(prev => ({ ...prev, selectedCustomer: selectedCustomer }));
    }
  }

  function handleRecipientInput(event: React.ChangeEvent<HTMLInputElement>) {
    const targetID = event.target.id;
    const targetValue = event.target.value;

    setNewRecipientState(prev => ({ ...prev, [targetID]: targetValue }));
  }

  function handleCloseTransferSheet() {
    setTicketState(prev => ({ ...prev, selectedTicketStubIDs: [], selectedTicketID: null }));
    setTransferTicketState({ openTransferTicket: false });
    setCustomerSearchState(defaultCustomerSearchState);
    setNewRecipientState(defaultNewRecipientState);
  }

  function handleCloseUnredeemSheet() {
    setTicketState(prev => ({ ...prev, selectedTicketStubIDs: [], selectedTicketID: null }));
    setUnredeemTicketState({ openUnredeemTicket: false });
  }

  function handleShowUnredeemSheet(stub: TicketStubType) {
    if (stub) {
      if (!ticketState.selectedTicketStubIDs.includes(stub.id)) {
        handleSelectTicketStub(stub.id);
      }
    }

    setUnredeemTicketState({ openUnredeemTicket: true });
  }

  async function processUnredeemTicketStub() {
    const unredeemTicketRes = await PutUnredeemStub({ id: ticketState.selectedTicketStubIDs[0] }, true);
    if (unredeemTicketRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error unredeeming ticket selected stub")));
      return;
    }

    dispatch(showSuccess(t("Ticket stub successfully unredeemed")));
    setUnredeemTicketState({ openUnredeemTicket: false });
    setTicketState(prev => ({ ...prev, selectedTicketStubIDs: [], selectedTicketID: null }));
    await loadTickets();
  }

  console.log("state", ticketState);
  // End Handlers
  return ticketsLoaded ? (
    <>
      <div className="mt-2">
        <h1 className="ui-customer_page-header mb-4">{t("Tickets")}</h1>
        <Divider />

        <div>
          {ticketState?.tickets?.length > 0
            ? ticketState.tickets.map((ticket: ITicket) => {
                const activeCount =
                  ticket.stubs?.filter(filteredStub => filteredStub.status === "valid" && !filteredStub?.parent_id)
                    .length || 0;
                const totalStubs = ticket?.stubs?.filter(stub => !stub?.parent_id)?.length || 0;
                return (
                  <Card
                    title={
                      <div className="flex items-center gap-4">
                        {ticket?.logo_image ? <img className="customer-ticket-logo" src={ticket?.logo_image} /> : null}
                        <div className="flex flex-col">
                          <span>{ticket.title}</span>
                          <span className="text-sm" style={{ color: "#475467" }}>
                            {ticket.subtitle}
                          </span>
                        </div>
                        <Badge type={"gray"} size="small">
                          {`${activeCount}/${totalStubs}`}
                        </Badge>
                      </div>
                    }
                    className={"card-height-override"}
                    key={ticket.id}
                    collapsable
                    defaultCollapsed={true}
                    sectioned
                  >
                    {/* Description */}
                    <div className="ticket-details"> {ticket.included} </div>
                    <div className="ticket-details">
                      <Markdown markdownText={ticket.description ?? ""} />
                    </div>

                    <div>
                      {ticket?.shared_with && ticket?.shared_with?.length > 0 ? (
                        <div>
                          <p className="ui-ticket-card-shared-with">
                            {t("Shared with: ")}
                            <Badge size="small" type="gray">
                              {ticket?.shared_with[0]?.shared_with_customer?.first_name?.charAt(0)}
                              {ticket?.shared_with[0]?.shared_with_customer?.last_name?.charAt(0)}
                            </Badge>{" "}
                            {ticket?.shared_with?.length > 1 ? `+ ${ticket?.shared_with?.length - 1}` : null}
                          </p>
                        </div>
                      ) : null}
                    </div>

                    {/* Action Bar */}
                    <TicketTransferActionBar
                      selectedLength={
                        ticket.stubs
                          ?.filter(stub => stub.status === "valid")
                          .some((stub: TicketStubType) => ticketState.selectedTicketStubIDs?.includes(stub.id))
                          ? ticketState.selectedTicketStubIDs.length
                          : 0
                      }
                      disabled={
                        ticket.stubs?.filter(stub => stub.status === "valid").length > 0 &&
                        ticket.stubs
                          ?.filter(stub => stub.status === "valid")
                          .some((stub: TicketStubType) => ticketState.selectedTicketStubIDs?.includes(stub.id))
                      }
                      onVoid={() => handleVoidTicketStub()}
                      onRedeem={() => handleRedeemTicketStub()}
                      onTransfer={() => handleShowTransferSheet()}
                      disableTransfer={ticket?.shared_with && ticket?.shared_with?.length > 0}
                    />

                    {/* Stubs Table */}
                    {ticket.stubs?.length > 0 ? (
                      // TODO: Add sort by status
                      // TODO: Add sort by code
                      <div className="transfer-data-table">
                        <DataTable
                          columns={[
                            {
                              label: "",
                              content: (
                                <Checkbox
                                  size="small"
                                  checked={
                                    ticketState?.selectedTicketStubIDs?.length > 0 &&
                                    ticket.stubs?.filter(stub => stub.status === "valid").length > 0 &&
                                    ticket.stubs
                                      ?.filter(stub => stub.status === "valid")
                                      .every((stub: TicketStubType) =>
                                        ticketState.selectedTicketStubIDs?.includes(stub.id),
                                      )
                                  }
                                  disabled={ticket?.stubs?.every((stub: TicketStubType) => stub.status !== "valid")}
                                  onChange={e => handleSelectAllTickets(e, ticket?.id)}
                                />
                              ),
                              width: "1%",
                            },
                            { label: t("Ticket Code") },
                            { label: t("Status") },
                            { label: "Remaining Redemptions" },
                            { label: "" },
                            { label: "" },
                            ...(ticket?.multi_use ? [{ label: "" }] : []),
                          ]}
                          loading={!ticketState?.tickets}
                        >
                          {ticket?.stubs
                            ?.filter(stub => !stub?.parent_id)
                            ?.map((stub: TicketStubType) => {
                              const childStubs = ticket?.multi_use
                                ? ticket?.stubs?.filter(childStub => childStub?.parent_id === stub?.id)
                                : [];
                              let badgeStatus: "primary" | "success" | "warning" | "error" | "gray";

                              let isDateExpired = false;

                              if (stub.expiry_date) {
                                isDateExpired = new Date(stub.expiry_date) < new Date(new Date().toDateString());
                              }

                              switch (stub.status) {
                                case "void":
                                  badgeStatus = "error";
                                  break;
                                case "expired":
                                  badgeStatus = "error";
                                  break;
                                case "redeemed":
                                  badgeStatus = "warning";
                                  break;
                                case "valid":
                                  badgeStatus = "success";
                                  break;
                                case "transfer_pending":
                                  badgeStatus = "gray";
                                  break;
                                case "transferred":
                                  badgeStatus = "gray";
                                  break;
                                default:
                                  break;
                              }

                              return (
                                <React.Fragment key={stub.id}>
                                  <tr
                                    onClick={
                                      ticket?.multi_use
                                        ? e => {
                                            e.stopPropagation();
                                            setExpandedStubId(expandedStubId === stub.id ? null : stub.id);
                                          }
                                        : undefined
                                    }
                                    className={ticket?.multi_use ? "cursor-pointer" : ""}
                                  >
                                    <td>
                                      <Checkbox
                                        size="small"
                                        checked={
                                          ticketState.selectedTicketStubIDs?.includes(stub.id) // Check if the stub ID is in the selectedTicketIDs
                                        }
                                        disabled={stub.status !== "valid" || stub?.is_shared}
                                        onChange={() => handleSelectTicketStub(stub.id, ticket.id)}
                                      />
                                    </td>
                                    <td>
                                      <div>
                                        <p className="stub-number-text">{stub.code}</p>
                                        <p className="ticket-term">
                                          {t("Pin")}: {stub.pin}
                                        </p>
                                      </div>
                                    </td>
                                    <td>
                                      {stub?.is_shared ? (
                                        <Badge size="small" type="lightblue">
                                          {t("Shared with you")}
                                        </Badge>
                                      ) : (
                                        <Badge
                                          type={badgeStatus}
                                          size="small"
                                          iconLeft={
                                            stub.status === "valid" || stub.status === "void" ? (
                                              <FontAwesomeIcon
                                                className="customer-ticket-badge-icon"
                                                icon={faCircle}
                                                size="xs"
                                              />
                                            ) : (
                                              <></>
                                            )
                                          }
                                        >
                                          {t(valueToString(stub.status))}
                                        </Badge>
                                      )}
                                    </td>

                                    <td>{stub?.redemptions_remaining}</td>

                                    <td>
                                      {stub.status === "transferred"
                                        ? t("Transferred on ")
                                        : stub.status === "transfer_pending"
                                        ? ""
                                        : stub.redeemed_at
                                        ? t("Redeemed on ")
                                        : isDateExpired
                                        ? t("Expired on ")
                                        : stub.expiry_date
                                        ? t("Expires on ")
                                        : ""}

                                      {stub?.status === "transferred" || stub?.status === "transfer_pending"
                                        ? moment(stub?.transfer?.accepted_at).format("MMMM Do, YYYY")
                                        : stub.redeemed_at
                                        ? moment(stub.redeemed_at).format("MMMM Do, YYYY")
                                        : stub.expiry_date
                                        ? moment(stub.expiry_date).format("MMMM Do, YYYY")
                                        : ""}
                                    </td>
                                    <td>
                                      {stub.status === "redeemed" &&
                                      !authStore?.user?.permissions?.tickets_unredeem ? null : (
                                        <NavigationDropdownNew
                                          rightAlign
                                          showPlainTextLabel
                                          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                          disabled={determineDisable(stub.status, stub?.is_shared)}
                                          sections={[
                                            [
                                              {
                                                label: t("Void"),
                                                icon: "ban",
                                                hidden: stub.status === "valid" ? false : true,
                                                onClick: () => handleVoidTicketStub(stub, false),
                                              },
                                              {
                                                label: t("Redeem"),
                                                icon: "ticket",
                                                hidden: stub.status === "valid" ? false : true,
                                                onClick: () => handleRedeemTicketStub(stub, false),
                                              },
                                              {
                                                label: t("Transfer"),
                                                icon: "envelope-open",
                                                hidden: stub.status === "valid" ? false : true,
                                                onClick: () => handleShowTransferSheet(stub, false),
                                                disabled:
                                                  (ticket?.shared_with && ticket?.shared_with?.length > 0) ||
                                                  ticket?.multi_use,
                                              },
                                              {
                                                label: t("Download (Standard)"),
                                                icon: "download",
                                                hidden: stub.status === "valid" ? false : true,
                                                onClick: () => handleTicketDownload(stub),
                                              },
                                              {
                                                label: t("Download (Zebra)"),
                                                icon: "download",
                                                hidden: stub.status === "valid" ? false : true,
                                                onClick: () => handleTicketDownload(stub, "zebra"),
                                              },
                                              {
                                                label: t("Unredeem"),
                                                icon: "ticket",
                                                hidden: stub.status !== "redeemed",
                                                onClick: () => handleShowUnredeemSheet(stub),
                                              },
                                              {
                                                label: t("Transfer Details"),
                                                icon: "arrow-right-arrow-left",
                                                hidden:
                                                  stub.status !== "transferred" && stub.status !== "transfer_pending",
                                                onClick: () =>
                                                  updateTransferDetails({
                                                    isOpen: true,
                                                    transferDetails: stub.transfer,
                                                  }),
                                              },
                                            ],
                                          ]}
                                        />
                                      )}
                                    </td>
                                    {ticket?.multi_use ? (
                                      <td>
                                        <button
                                          className="stub-expand-button"
                                          onClick={e => {
                                            e.stopPropagation();
                                            setExpandedStubId(expandedStubId === stub.id ? null : stub.id);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={expandedStubId === stub.id ? faChevronUp : faChevronDown}
                                          />
                                        </button>
                                      </td>
                                    ) : null}
                                  </tr>
                                  {/* Display redemption dates of multi use stubs */}
                                  {expandedStubId === stub?.id
                                    ? childStubs
                                        ?.filter(childStub => childStub?.redeemed_at)
                                        ?.map((childStub, i, filtreredArray) => {
                                          const [redeemDate, redeemTime] = childStub.redeemed_at.split(" ");
                                          return (
                                            <tr
                                              key={i}
                                              className={
                                                i === filtreredArray?.length - 1 ? "" : "ticket-redemption-row"
                                              }
                                            >
                                              <td>
                                                <FontAwesomeIcon className="ml-4" icon={faArrowTurnDownRight} />
                                              </td>
                                              <td>{t("Redemption #{{redemptionNum}}", { redemptionNum: i + 1 })}</td>
                                              <td>
                                                <Badge size="small" type="warning">
                                                  {t("Redeemed")}
                                                </Badge>
                                              </td>
                                              <td>
                                                {moment(redeemDate, "YYYY-MM-DD")?.format("MMMM Do, YYYY")}{" "}
                                                {moment.utc(redeemTime, "HH:mm")?.local()?.format("LT")}
                                              </td>
                                              <td></td>
                                              <td></td>
                                              <td>
                                                <Button
                                                  type="text"
                                                  size="xsmall"
                                                  onClick={() => handleShowUnredeemSheet(childStub)}
                                                >
                                                  {t("Unredeem")}
                                                </Button>
                                              </td>
                                            </tr>
                                          );
                                        })
                                    : null}
                                </React.Fragment>
                              );
                            })}
                        </DataTable>
                      </div>
                    ) : null}
                  </Card>
                );
              })
            : null}
        </div>

        {/* Redeem Popup */}
        <Popup
          open={redeemTicketState.openRedeemTicket}
          type="warning"
          onCancel={() => setRedeemTicketState({ openRedeemTicket: false })}
          title={t("Redeem Ticket Stub")}
          description={t("Are you sure you want to redeem the selected ticket stub(s)?")}
          onOk={() => {
            void processRedeemTicketStub(ticketState.selectedTicketStubIDs);
          }}
          okText={t("Redeem")}
          cancelText={t("Cancel")}
        />

        {/* Void Popup */}
        <Popup
          open={voidTicketState.openVoidTicket}
          type="warning"
          onCancel={() => setVoidTicketState({ openVoidTicket: false })}
          title={t("Void Ticket Stub")}
          description={t("Are you sure you want to void the selected ticket stub?")}
          onOk={() => {
            void processVoidTicketStub(ticketState.selectedTicketStubIDs);
          }}
          okText={t("Void")}
          cancelText={t("Cancel")}
        />

        {/* Stub Transfer Details */}
        <Portal isMounted={transferDetails.isOpen}>
          <Sheet
            title={t("Stub Transfer Details")}
            open={transferDetails.isOpen}
            fullWidthOK
            onClose={closeTransferDetails}
            okText={t("Ok")}
            onOk={closeTransferDetails}
            hideCancelButton
            backDropCancel
          >
            <div className="flex flex-col">
              <div className="flex flex-row gap-1 items-center">
                <>
                  <span className="text-sm">{t("Recipient: ")}</span>
                </>
                {transferDetails.transferDetails?.recipient?.full_name}
              </div>
              <div className="flex flex-row gap-1 items-center">
                <>
                  <span className="text-sm"> {t("Reference Number: ")}</span>
                  {transferDetails?.transferDetails?.reference_number}
                </>
              </div>
              <div className="flex flex-row gap-1 items-center">
                <span className="text-sm">
                  {transferDetails?.transferDetails?.accepted_at ? t("Accepted on: ") : t("Cancelled on: ")}
                </span>
                {moment(
                  transferDetails?.transferDetails?.cancelled_at ?? transferDetails?.transferDetails?.accepted_at,
                ).format("MMMM Do, YYYY")}
              </div>
            </div>
          </Sheet>
        </Portal>

        {/* Transfer Sheet */}
        <Portal isMounted={transferTicketState.openTransferTicket}>
          <Sheet
            title={t("Transfer Ticket Stub")}
            open={transferTicketState.openTransferTicket}
            size="small"
            overflow
            okText={t("Transfer Ticket")}
            okDisabled={!isEmail(newRecipientState.emailAddress) && customerSearchState?.selectedCustomer === undefined}
            onOk={() => handleProcessStubTransfer()}
            onCancel={() => handleCloseTransferSheet()}
          >
            <>
              <div className="flex flex-col gap-4 pb-2">
                <p className="text-sm">{t("Select a customer to transfer the ticket stub(s) to:")}</p>
              </div>
              <Select
                label={t("Customer Name")}
                onChange={(value: string, extraValues: ICustomer) => handleCustomerSelect(value, extraValues)}
                showSearch
                allowClear
                searchValue={customerSearchState.search}
                onSearch={(search: string) => setCustomerSearchState(prev => ({ ...prev, search: search }))}
                searching={customerSearchState.customers === undefined}
                handleClear={() => setCustomerSearchState(defaultCustomerSearchState)}
                placeholder={t("Search customers by name, address or phone")}
              >
                {customerSearchState?.customers?.length !== 0 ? (
                  customerSearchState.customers?.map(customer => (
                    <Select.Option value={customer.id} key={customer.id} extraValues={customer}>
                      <div className="flex justify-between">
                        <div>
                          <div className="font-semibold text-lg">{customer?.full_name}</div>
                          <div className="text-sm text-gray-500">{customer.customer_type}</div>
                          <div className="text-sm text-gray-500">{customer.email}</div>
                          <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                        </div>

                        <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                      </div>
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option value={"new"} key={0} extraValues={null}>
                    {t("New Customer")}
                  </Select.Option>
                )}
              </Select>

              {/* Selected Customer */}
              {customerSearchState?.selectedCustomer ? (
                <GolferCard
                  name={customerSearchState?.selectedCustomer?.first_name}
                  customerType={customerSearchState?.selectedCustomer?.customer_type}
                  email={customerSearchState?.selectedCustomer?.email}
                  phone={customerSearchState?.selectedCustomer?.phone}
                  closable
                  removeGolfer={() =>
                    setCustomerSearchState(prev => ({ ...prev, selectedCustomer: undefined, search: "" }))
                  }
                />
              ) : null}

              {/* New Recipient */}
              {customerSearchState.showNewRecipient && customerSearchState.selectedCustomer === undefined ? (
                <>
                  <Divider />
                  <h2 style={{ paddingBottom: "1rem" }}>{t("Add New Recipient")}</h2>
                  <div className="flex flex-row gap-4 pb-4">
                    <div className="flex-1">
                      <Input
                        id={"firstName"}
                        label={t("First Name")}
                        placeholder={t("First Name")}
                        value={newRecipientState?.firstName}
                        onChange={e => handleRecipientInput(e)}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        id={"lastName"}
                        label={t("Last Name")}
                        placeholder={t("Last Name")}
                        value={newRecipientState?.lastName}
                        onChange={e => handleRecipientInput(e)}
                      />
                    </div>
                  </div>

                  <div>
                    <Input
                      id={"emailAddress"}
                      label={t("Email")}
                      placeholder={t("Enter Recipient Email")}
                      value={newRecipientState?.emailAddress}
                      onChange={e => handleRecipientInput(e)}
                      error={!isEmail(newRecipientState.emailAddress) && newRecipientState.emailAddress !== ""}
                    />
                  </div>
                </>
              ) : null}
            </>
          </Sheet>
        </Portal>

        <Popup
          open={unredeemTicketState.openUnredeemTicket}
          type="warning"
          onCancel={() => handleCloseUnredeemSheet()}
          title={t("Unredeem Ticket")}
          description={t("Are you sure you want to unredeem this ticket stub?")}
          onOk={() => {
            void processUnredeemTicketStub();
          }}
          okText={t("Unredeem")}
          cancelText={t("Cancel")}
        />
      </div>
    </>
  ) : (
    <div style={{ overflow: "hidden", height: "50px" }}>
      <Spin />
    </div>
  );
}
